/**
 * Final-countdown urgency model.
 * Last 5 minutes: timer ramps yellow (#ffd23f @5:00) → orange (#ff8a1f @~2:30) → red (#ff2a3a @≤1:00),
 * interpolated continuously. A +30s buy eases the color back up (smoothed, not snapped).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const URGENCY_WINDOW = 300;
export const FINAL_MINUTE = 60;
export const FINAL_TEN = 10;

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const STOPS: Array<[number, RGB]> = [
  [300, hex('#ffd23f')],
  [150, hex('#ff8a1f')],
  [60, hex('#ff2a3a')],
  [0, hex('#ff2a3a')],
];

/** Continuous urgency color for a (possibly fractional) seconds value. */
export function urgencyRGB(t: number): RGB {
  if (t >= STOPS[0][0]) return STOPS[0][1];
  for (let i = 0; i < STOPS.length - 1; i++) {
    const [t0, c0] = STOPS[i];
    const [t1, c1] = STOPS[i + 1];
    if (t <= t0 && t >= t1) {
      const k = (t0 - t) / (t0 - t1 || 1);
      return [0, 1, 2].map(j => Math.round(c0[j] + (c1[j] - c0[j]) * k)) as RGB;
    }
  }
  return STOPS[STOPS.length - 1][1];
}
export const rgbStr = ([r, g, b]: RGB, a = 1) => (a >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`);

export interface UrgencyState {
  /** smoothed seconds used for color (eases back up after a +30s buy) */
  smoothT: number;
  /** 0 (≥5:00 left) … 1 (0:00) */
  level: number;
  active: boolean;
  rgb: RGB;
  color: string;
}

const calm = (): UrgencyState => ({ smoothT: 1e9, level: 0, active: false, rgb: hex('#46def8'), color: '#46def8' });

export function computeUrgency(smoothT: number): UrgencyState {
  if (!(smoothT >= 0) || smoothT >= URGENCY_WINDOW) return { ...calm(), smoothT };
  const rgb = urgencyRGB(smoothT);
  return { smoothT, level: Math.min(1, Math.max(0, (URGENCY_WINDOW - smoothT) / URGENCY_WINDOW)), active: true, rgb, color: rgbStr(rgb) };
}

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Smooths the timer into an urgency state at ~30fps. Also exposes a ref so the
 * Three.js loop can read it without React re-renders. Writes CSS vars on <html>:
 * --u-color, --u-level, --u-beat (heartbeat period).
 */
export function useUrgency(timeRemaining: number) {
  const ref = useRef<UrgencyState>(calm());
  const target = useRef(timeRemaining);
  target.current = timeRemaining;
  const [state, setState] = useState<UrgencyState>(calm());

  useEffect(() => {
    let raf = 0, last = 0, lastPush = 0;
    let s = target.current < 0 ? 1e9 : target.current;
    const root = document.documentElement;
    const loop = (ts: number) => {
      raf = requestAnimationFrame(loop);
      if (ts - last < 33) return; // ~30fps cap
      last = ts;
      const t = target.current < 0 ? 1e9 : target.current;
      // Countdown follows tightly; upward jumps (+30s buys) ease back over ~1s.
      if (t > s) s += Math.max((t - s) * 0.09, 0.05);
      else s = t;
      if (Math.abs(t - s) < 0.05) s = t;
      const u = computeUrgency(s > URGENCY_WINDOW * 4 ? 1e9 : s);
      ref.current = u;
      root.style.setProperty('--u-color', u.active ? u.color : '#46def8');
      root.style.setProperty('--u-rgb', u.rgb.join(','));
      root.style.setProperty('--u-level', u.level.toFixed(3));
      root.style.setProperty('--u-beat', `${(1.6 - 1.2 * u.level).toFixed(2)}s`);
      root.classList.toggle('nt3d-urgent', u.active);
      if (ts - lastPush > 120) { lastPush = ts; setState(prev => (Math.abs(prev.smoothT - u.smoothT) > 0.05 || prev.active !== u.active ? u : prev)); }
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); root.classList.remove('nt3d-urgent'); };
  }, []);

  return { urgency: state, urgencyRef: ref };
}

/**
 * Dev/demo preview of the final 5 minutes: `?preview=final` (optionally `&t=120`) or Ctrl+Shift+F.
 * Runs a local, display-only clock; never touches the contract.
 */
export function useFinalPreview() {
  const initial = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('preview') === 'final') return Math.min(URGENCY_WINDOW, Math.max(1, Number(p.get('t')) || URGENCY_WINDOW)); if (p.get('preview') === 'clock') return Math.min(86400, Math.max(1, Number(p.get('t')) || 1421)); // any-time local clock preview
    } catch { /* ignore */ }
    return null;
  })();
  const [t, setT] = useState<number | null>(initial);
  const enabled = t !== null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        setT(prev => (prev === null ? URGENCY_WINDOW : null));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const iv = setInterval(() => setT(prev => (prev === null ? null : prev <= 1 ? URGENCY_WINDOW : prev - 1)), 1000);
    return () => clearInterval(iv);
  }, [enabled]);

  const simulateBuy = useCallback(() => setT(prev => (prev === null ? prev : Math.min(86400, prev + 30))), []);
  const jump = useCallback((s: number) => setT(s), []);
  const exit = useCallback(() => setT(null), []);
  return { enabled, t: t ?? 0, simulateBuy, jump, exit };
}
